/**
 * The served verification API — request dispatch, authorization, idempotency.
 *
 * ── Why this exists ──
 *
 * `api-catalog.ts` describes fourteen endpoints and `api-sdk.ts` plans requests
 * against them, but nothing served them: the catalogue was a catalogue. The
 * buyer gauntlet's engineer persona buys as a design partner precisely because
 * he still has to build the client, the retries, and the delivery himself
 * (blocker F4, criterion B2). This module is the server half.
 *
 * ── Why it is pure ──
 *
 * `dispatchVerificationApiRequest` takes an HTTP-shaped request and returns an
 * HTTP-shaped response. It never touches a socket, a clock, or a database; the
 * data layer arrives as an injected `VerificationApiStore` and the time as a
 * string. That is what lets the gate drive every branch — including the
 * cross-tenant and replay-conflict branches a live server would rarely produce
 * — and it lets `excellent/server/verification-api-routes.ts` be a thin mount
 * rather than a second implementation of the rules.
 *
 * ── The security decision encoded here ──
 *
 * `receipts.issue` is **removed from the public surface**. The gauntlet's CISO
 * found it reachable by any token holding `receipts:write`, and a receipt is
 * the product's atom of trust: if a compromised token can mint one, the whole
 * chain means nothing. Receipts are minted by the engine over a run. The
 * removal is enforced two ways — the endpoint is absent from
 * `PUBLIC_VERIFICATION_API_ENDPOINTS`, *and* its concrete path is refused by
 * the router before authorization runs. Either alone would be a catalogue edit
 * that a future route-table change could undo.
 */
import { UNIMPLEMENTED_PUBLIC_OPERATIONS, VERIFICATION_API_BASE_PATH, VERIFICATION_API_ENDPOINTS, } from "./api-catalog.js";
import { authorizeVerificationApiRequest, } from "./api-auth.js";
/* ── The public surface ──────────────────────────────────────────────────── */
/**
 * Operation ids that are engine-internal and must never be reachable by a
 * token, however scoped.
 *
 * A `Set` rather than a filter predicate so the reason is enumerable: a gate,
 * a docs page, and the API explorer can all ask "what is deliberately not
 * served, and why" and get the same answer.
 */
export const ENGINE_ONLY_OPERATIONS = {
    "receipts.issue": "A receipt is minted by the engine over a completed run, never by an API caller. A token that could issue one could manufacture proof, which would make every other receipt worthless.",
};
/** The catalogue as actually served. */
export const PUBLIC_VERIFICATION_API_ENDPOINTS = VERIFICATION_API_ENDPOINTS.filter((e) => !(e.id in ENGINE_ONLY_OPERATIONS));
/** Concrete paths that are refused before authorization, by method. */
export const ENGINE_ONLY_ROUTES = VERIFICATION_API_ENDPOINTS.filter((e) => e.id in ENGINE_ONLY_OPERATIONS).map((e) => ({
    method: e.method,
    path: e.path,
}));
/**
 * THE QUERY FILTERS THIS OPERATION ADVERTISES — derived, never declared twice.
 *
 * ── The defect this exists to close ──
 *
 * Thirteen served GET operations published 26 query filters and the API read
 * NONE of them. A caller who sent `?status=open` got every row in the tenant
 * back, with a 200 and no word about it. They did not learn they had been
 * ignored; they learned later, from a wrong number on a page, or never. That
 * is the same false-publication class the catalogue's request examples were
 * fixed for, one layer down: the promise was in the summary prose, in the
 * published example and in `requestFieldsIn`, and the implementation was in
 * none of the three.
 *
 * Silently ignoring is the one answer that is never right. Applying the filter
 * is right where the field is real; refusing it is right where it is not. Both
 * need the same fact first — WHICH parameters this operation advertises — and
 * that fact already exists exactly once.
 *
 * ── Why this is derived from the example rather than a new field ──
 *
 * `requestFieldsIn: "query"` already MEANS "the example's non-path keys are
 * query-string filters" — that is the type's published definition, and
 * `check-verification-api-request-examples` L2 already refuses when the
 * declaration and an independent derivation of it disagree. So the advertised
 * filter set is not a new thing to declare; it is a thing already declared,
 * and reading it here is what makes the declaration load-bearing rather than
 * decorative.
 *
 * A fourth field (`queryFilters: [...]`) would have been a fourth place to
 * drift from the other three. This cannot drift: the example a caller copies
 * IS the set the dispatcher admits IS the set the handler filters on.
 */
export function advertisedQueryFilters(endpoint) {
    if (endpoint.requestFieldsIn !== "query")
        return [];
    const pathParams = new Set([...endpoint.path.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]));
    return Object.keys(endpoint.sdk?.requestExample ?? {}).filter((k) => !pathParams.has(k));
}
/**
 * How long a reservation is held before it may be taken over.
 *
 * This is the crash budget: a holder that claims a key and dies leaves a row
 * that is neither complete nor free, and only the clock can free it. Long
 * enough that no honest handler is mistaken for a corpse (the served handlers
 * are synchronous SQLite writes measured in single-digit milliseconds), short
 * enough that a crash does not wedge a key for a human-noticeable time.
 *
 * An expired lease is NOT a completed request. It is an unused key with a
 * corpse on it: the taker-over runs the handler itself and stores its own
 * response. Reading an expired reservation as "completed with no response"
 * would hand a caller a 200 carrying nothing, which is the worst of the three
 * possible answers — it claims an effect that never happened.
 */
export const VERIFICATION_IDEMPOTENCY_LEASE_MS = 30000;
/* ── Canonicalization ────────────────────────────────────────────────────── */
/**
 * Stable stringification for the idempotency request hash.
 *
 * Key order must not change the hash, or a client that serializes its object
 * differently on a retry would get a spurious 409 on a request that is in fact
 * identical — which would make the retry path worse than no retry path.
 */
export function canonicalizeRequestBody(body) {
    const walk = (node) => {
        if (Array.isArray(node))
            return node.map(walk);
        if (node && typeof node === "object") {
            const entries = Object.entries(node)
                .filter(([, v]) => v !== undefined)
                .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
            const out = {};
            for (const [k, v] of entries)
                out[k] = walk(v);
            return out;
        }
        return node;
    };
    return JSON.stringify(walk(body ?? null));
}
/**
 * A non-cryptographic content hash for idempotency comparison.
 *
 * FNV-1a, not SHA-256: this hash never leaves the process and guards a
 * same-key-different-body check, not an integrity claim. Receipt hashing —
 * where the claim *is* integrity — uses SHA-256 in `receipt-service.ts`, and
 * conflating the two would invite someone to reuse this one there.
 */
export function requestBodyHash(body, target) {
    const s = canonicalizeRequestBody({ body: body ?? null, target: target ?? "" });
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `fnv1a-${h.toString(16).padStart(8, "0")}`;
}
/** Split a request path into segments, dropping any query string. */
function segments(path) {
    const clean = String(path ?? "").split("?")[0] ?? "";
    return clean.split("/").filter((s) => s.length > 0);
}
function parseQuery(path) {
    const qs = String(path ?? "").split("?")[1];
    if (!qs)
        return {};
    const out = {};
    for (const pair of qs.split("&")) {
        if (!pair)
            continue;
        const idx = pair.indexOf("=");
        const rawKey = idx < 0 ? pair : pair.slice(0, idx);
        const rawVal = idx < 0 ? "" : pair.slice(idx + 1);
        try {
            out[decodeURIComponent(rawKey)] = decodeURIComponent(rawVal);
        }
        catch {
            out[rawKey] = rawVal;
        }
    }
    return out;
}
/**
 * Match a method+path against a candidate endpoint list.
 *
 * Takes the list as an argument rather than closing over the public one so the
 * caller can ask the question of the *full* catalogue too — which is how the
 * router distinguishes "no such route" (404, honest) from "this route exists
 * but is engine-only" (404, with a reason the operator can read in the log).
 */
export function matchVerificationRoute(method, path, endpoints) {
    const reqMethod = String(method ?? "").toUpperCase();
    const reqSegs = segments(path);
    for (const endpoint of endpoints) {
        if (endpoint.method !== reqMethod)
            continue;
        const tplSegs = segments(endpoint.path);
        if (tplSegs.length !== reqSegs.length)
            continue;
        const params = {};
        let matched = true;
        for (let i = 0; i < tplSegs.length; i += 1) {
            const tpl = tplSegs[i] ?? "";
            const got = reqSegs[i] ?? "";
            if (tpl.startsWith("{") && tpl.endsWith("}")) {
                const name = tpl.slice(1, -1);
                if (!got) {
                    matched = false;
                    break;
                }
                params[name] = got;
                continue;
            }
            if (tpl !== got) {
                matched = false;
                break;
            }
        }
        if (matched)
            return { endpoint, params };
    }
    return null;
}
/* ── Tenant identifiers ──────────────────────────────────────────────────── */
/** The namespace half of a tenant id — everything before the first `:`, else "". */
function tenantNamespace(tenantId) {
    const idx = tenantId.indexOf(":");
    return idx < 0 ? "" : tenantId.slice(0, idx);
}
/**
 * Could the tenant the caller NAMED denote a sibling of the credential's tenant?
 *
 * ── The two failures this separates, and why conflating them cost a sale ──
 *
 * `token.tenantId !== request.tenantId` is one comparison hiding two completely
 * different events:
 *
 *  (a) The caller sent a value that is not a tenant reference at all — `6`, a
 *      bare workspace ULID, `abc`. Nobody is probing anything; the client is
 *      simply built wrong.
 *  (b) The caller sent a well-formed id for a DIFFERENT tenant — `workspace:9`
 *      from a `workspace:6` credential. That is a cross-tenant probe.
 *
 * Reporting (a) the way (b) is reported — as `no such route` — is a lie about
 * the wrong subsystem, and it is a lie the caller cannot see through: the route
 * exists, the handler exists, only the header was wrong. An independent
 * evaluator sent `X-Excellent-Tenant: 6`, got that 404 on all 31 endpoints and
 * concluded the product was unmounted. The URL was right the whole time.
 *
 * So (a) gets an honest refusal naming the header and the form, and (b) keeps
 * the existence-opaque 404 that the threat model, the adversarial gate and the
 * published posture statement all commit to.
 *
 * ── Why the test is the NAMESPACE and not a hardcoded `workspace:` prefix ──
 *
 * The API mandates no tenant grammar: `verification_api_tokens` accepts any
 * non-empty string, and deployments (and every gate fixture) use ids like
 * `tenant_northwind`. A literal `workspace:` requirement would refuse callers
 * whose tenant genuinely matches their token, which is a far worse bug than the
 * one being fixed. Comparing namespaces asks the question that actually matters
 * — "is this the identifier space my credential lives in?" — and answers it
 * correctly for a prefix-less deployment too, where both sides are "".
 *
 * Discloses nothing: the namespace compared against is the caller's OWN token's,
 * and no branch here consults any tenant registry, so the answer is identical
 * whether the named tenant exists or not.
 */
function namesASiblingTenant(presented, credentialTenantId) {
    const ns = tenantNamespace(credentialTenantId);
    if (tenantNamespace(presented) !== ns)
        return false;
    // Same namespace, but the local half has to actually be there: `workspace:`
    // with nothing after it names no tenant, so it is a malformed value (a), not a
    // sibling (b).
    return presented.length > (ns ? ns.length + 1 : 0);
}
/**
 * How to tell the caller to shape the value, derived from the credential they
 * presented — so the advice is right in a deployment whose tenant ids carry no
 * namespace, instead of sending them after a `workspace:` prefix that does not
 * exist there. The credential is their own, so this states nothing they do not
 * already hold.
 */
function expectedTenantForm(credentialTenantId) {
    const ns = tenantNamespace(credentialTenantId);
    return ns
        ? `Send the tenant's full id in the form "${ns}:<id>"`
        : "Send the tenant id exactly as it was issued";
}
/* ── Header access ───────────────────────────────────────────────────────── */
export function readHeader(headers, name) {
    if (!headers)
        return null;
    const target = name.toLowerCase();
    for (const [k, v] of Object.entries(headers)) {
        if (k.toLowerCase() === target)
            return v;
    }
    return null;
}
/* ── Errors ──────────────────────────────────────────────────────────────── */
function fail(status, code, message, extra = {}) {
    const error = { code, message };
    if (extra.requiredScope)
        error.requiredScope = extra.requiredScope;
    if (extra.operation)
        error.operation = extra.operation;
    return {
        status,
        body: { error },
        headers: { "content-type": "application/json", ...(extra.headers ?? {}) },
        operation: extra.operation ?? null,
        replayed: false,
    };
}
/* ── The dispatcher ──────────────────────────────────────────────────────── */
/**
 * Turn a request into a response.
 *
 * Order of checks is deliberate and load-bearing:
 *
 *  1. **Engine-only routes** are refused first, before authentication. A
 *     404 that depends on the token would tell an attacker with a bad token
 *     less than a 404 that does not — and more importantly, refusing before
 *     auth means no scope, however broad, can reach the route. `verification:*`
 *     included.
 *  2. **Route resolution** — 404 for an unknown path.
 *  3. **Authentication** — 401 with no token, for reads as well as writes.
 *     There is no ambient authority anywhere in this surface.
 *  4. **Tenant** — a caller must name the tenant it means. Naming none is a
 *     400; naming a value that is not a tenant reference at all is a 403 that
 *     names the header and the form, because reporting a client-side header bug
 *     as a missing route sends the caller to debug the wrong subsystem.
 *  5. **Authorization** — 403 naming the missing scope. A well-formed id for
 *     someone else's tenant stays a 404: that one IS a probe, and existence is
 *     the leak.
 *  6. **Idempotency** — required on writes, checked before the handler runs so
 *     a replay cannot produce a second effect.
 *  7. **Handler** — and a handler-level "not yours" is reported as 404, never
 *     403, because 403 confirms the object exists in another tenant.
 */
export function dispatchVerificationApiRequest(input) {
    const { request, store } = input;
    const method = String(request.method ?? "").toUpperCase();
    const path = String(request.path ?? "");
    // 1. Engine-only routes: refused before anything else looks at the token.
    const engineOnly = matchVerificationRoute(method, path, VERIFICATION_API_ENDPOINTS.filter((e) => e.id in ENGINE_ONLY_OPERATIONS));
    if (engineOnly) {
        return fail(404, "not_found", ENGINE_ONLY_OPERATIONS[engineOnly.endpoint.id] ??
            "This operation is engine-internal and is not served.");
    }
    // 2. Route resolution.
    if (!path.startsWith(VERIFICATION_API_BASE_PATH)) {
        return fail(404, "not_found", `No verification API route for ${method} ${path}.`);
    }
    const match = matchVerificationRoute(method, path, PUBLIC_VERIFICATION_API_ENDPOINTS);
    if (!match) {
        return fail(404, "not_found", `No verification API route for ${method} ${path}.`);
    }
    const { endpoint, params } = match;
    // 3. Authentication — reads are not exempt.
    const token = request.token ?? null;
    if (!token) {
        return fail(401, "unauthorized", "A bearer token is required for every verification API call.", {
            operation: endpoint.id,
        });
    }
    // 4. Tenant.
    const tenantId = request.tenantId ?? null;
    if (!tenantId) {
        return fail(400, "bad_request", "The request must name the tenant it applies to.", {
            operation: endpoint.id,
        });
    }
    // 5. Authorization.
    const auth = authorizeVerificationApiRequest({
        token,
        request: {
            tenantId,
            resource: endpoint.resource,
            action: endpoint.action,
            now: request.now,
        },
    });
    if (!auth.ok) {
        if (auth.reason === "tenant-mismatch") {
            // A value that could not denote a tenant at all is a malformed header, not
            // a cross-tenant probe. Say so, name the header and the form, and let the
            // caller fix the call without reading this file.
            if (!namesASiblingTenant(tenantId, token.tenantId)) {
                return fail(403, "tenant_unresolved", `The X-Excellent-Tenant header did not resolve to a tenant this credential may act on. ` +
                    `${expectedTenantForm(token.tenantId)} — a bare id, name or ULID is not accepted. ` +
                    `This refusal reports only that the header did not resolve for the credential presented; it does not say whether any particular tenant exists.`, { operation: endpoint.id });
            }
            // A token for another tenant learns nothing about this one.
            return fail(404, "not_found", `No verification API route for ${method} ${path}.`, {
                operation: endpoint.id,
            });
        }
        if (auth.reason === "token-revoked" || auth.reason === "token-expired") {
            return fail(401, "unauthorized", `The token is ${auth.reason.replace("token-", "")}.`, {
                operation: endpoint.id,
            });
        }
        return fail(403, "forbidden", `This token lacks the ${endpoint.requiredScope} scope required by ${endpoint.id}.`, { operation: endpoint.id, requiredScope: endpoint.requiredScope });
    }
    const query = parseQuery(path);
    // 5b. A QUERY PARAMETER THIS OPERATION DOES NOT ADVERTISE IS REFUSED.
    //
    // The alternative is what stood here until now: accept it, ignore it, answer
    // 200. That answer is indistinguishable from having applied it, which makes
    // it worse than an error — the caller proceeds on a number that is wrong in
    // a direction they cannot see. An explicit 400 naming the parameter costs a
    // caller one round trip and tells them the truth.
    //
    // It lives in the DISPATCHER, not in the handlers, for two reasons. It holds
    // both halves of the relation here — the catalogue entry that advertises and
    // the request that asks — so nothing has to be kept in step. And a handler
    // added tomorrow cannot forget it: there is no per-operation opt-in to omit.
    // The complement, that an ADVERTISED filter is actually applied, is a
    // property of the handler and is proved by
    // `check-verification-api-request-examples` L5.
    const advertised = advertisedQueryFilters(endpoint);
    const unsupported = Object.keys(query).filter((name) => !advertised.includes(name));
    if (unsupported.length > 0) {
        return fail(400, "unsupported_query_parameter", `${endpoint.id} does not support the query parameter${unsupported.length > 1 ? "s" : ""} ` +
            `${unsupported.map((n) => `\`${n}\``).join(", ")}. ` +
            (advertised.length > 0
                ? `It filters on ${advertised.map((n) => `\`${n}\``).join(", ")} and nothing else.`
                : `It takes no query filters.`) +
            ` This is refused rather than ignored on purpose: a filter that is accepted and ` +
            `discarded returns every row with a 200, and you would have had no way to tell that ` +
            `from a filter that matched everything.`, { operation: endpoint.id });
    }
    const isWrite = endpoint.action === "write";
    const idemKeyHeader = readHeader(request.headers, "idempotency-key");
    // 6. Idempotency, before the handler so a replay cannot double-apply.
    let idemKey = null;
    let requestHash = "";
    let claimLease = null;
    if (isWrite) {
        if (!idemKeyHeader || !idemKeyHeader.trim()) {
            return fail(400, "idempotency_key_required", "Write operations require an Idempotency-Key header so a retry cannot apply twice.", { operation: endpoint.id });
        }
        idemKey = {
            tenantId,
            tokenId: auth.tokenId,
            operation: endpoint.id,
            key: idemKeyHeader.trim(),
        };
        requestHash = requestBodyHash(request.body, `${endpoint.method} ${String(request.path ?? "").split("?")[0]}`);
        // THE CLAIM. Not a read — a write that either wins or loses, so that two
        // concurrent requests carrying one key cannot both be told the key is free.
        const claim = store.idempotency.reserve(idemKey, {
            requestHash,
            now: request.now,
            leaseMs: VERIFICATION_IDEMPOTENCY_LEASE_MS,
        });
        // A DIFFERENT BODY under this key is refused first, in both the completed
        // and the in-flight case, and refused the SAME way. The order is the point:
        // "come back later" would be a lie to a caller whose request is never going
        // to be accepted under this key however long it waits. Waiting is only
        // honest when waiting can change the answer.
        if (claim.outcome !== "reserved") {
            const priorHash = claim.outcome === "replay" ? claim.record.requestHash : claim.requestHash;
            if (priorHash !== requestHash) {
                return fail(409, "idempotency_key_conflict", "This Idempotency-Key was already used with a different request body.", { operation: endpoint.id });
            }
        }
        if (claim.outcome === "replay") {
            return {
                status: claim.record.status,
                body: claim.record.body,
                headers: {
                    "content-type": "application/json",
                    "idempotency-replayed": "true",
                },
                operation: endpoint.id,
                replayed: true,
            };
        }
        if (claim.outcome === "in-progress") {
            // ── WHAT A LOSING CLAIMANT GETS, AND WHY IT IS A REFUSAL ──
            //
            // The alternative is to WAIT for the winner and then hand back the
            // winner's stored response. It is rejected here for three reasons, in
            // increasing order of seriousness:
            //
            //  1. This dispatcher is SYNCHRONOUS by contract — `reserve` returns a
            //     value, it does not return a promise — and it is synchronous so that
            //     it can be gated without a database. A `reserve` that waited would
            //     have to block, and the host is single-threaded: one same-key
            //     collision would stall every other tenant's request behind it. That
            //     converts a two-request contention into a whole-host stall.
            //  2. The wait has no bound this code owns. The winner's handler runs in
            //     another process; how long it takes is not ours to promise.
            //  3. The caller IS a retrying client — that is the only kind of caller
            //     that sends the same key twice at once. It already has the machinery
            //     to come back, and a refusal that creates NOTHING is exactly the
            //     signal it needs. At-most-once is preserved either way; only this way
            //     preserves it without borrowing the host's whole event loop.
            //
            // So: 409, its own code (never the conflict code — a caller must be able
            // to tell "your body differs, stop" from "the original is still running,
            // come back"), and a `retry-after` so the retry is a scheduled one rather
            // than a hot loop.
            return fail(409, "idempotency_key_in_progress", "Another request with this Idempotency-Key is still in flight. Nothing was applied for this request; retry it.", {
                operation: endpoint.id,
                headers: {
                    "retry-after": "1",
                    "idempotency-lease-expires-at": claim.leaseExpiresAt,
                },
            });
        }
        claimLease = claim.lease;
    }
    /**
     * Give the key back.
     *
     * Called on every path that reserved and then did NOT apply an effect. The
     * pre-existing contract is that only a SUCCESSFUL write burns a key — a
     * refused request must leave the key usable, or a client that fixes its body
     * could never retry. Releasing restores that byte for byte across the new
     * protocol.
     */
    const releaseClaim = () => {
        if (idemKey && claimLease !== null)
            store.idempotency.release(idemKey, claimLease);
    };
    // 7. Handler.
    const handler = store.handlers[endpoint.id];
    if (!handler) {
        // Checked here rather than before the claim on purpose: a key that was
        // completed by a deployment that HAD the handler must still replay after a
        // deployment that does not. Moving this check above the claim would answer
        // a legitimate replay with a 501.
        releaseClaim();
        return fail(501, "not_implemented", `${endpoint.id} is in the served catalogue but this deployment has no handler for it.`, { operation: endpoint.id });
    }
    let result;
    try {
        result = handler({
            operation: endpoint.id,
            endpoint,
            params,
            query,
            body: request.body,
            tenantId,
            tokenId: auth.tokenId,
            now: request.now,
        });
    }
    catch (error) {
        // A throwing handler is the crash case in miniature. Free the key so the
        // retry this caller is about to send is not refused for the lease's whole
        // duration — but never let a failure to free it replace the real error,
        // which is the one the operator has to read. An unreleased reservation is
        // survivable: the lease expires.
        try {
            releaseClaim();
        }
        catch {
            /* the lease is the backstop */
        }
        throw error;
    }
    if (!result.ok) {
        releaseClaim();
        return fail(result.status, result.code, result.message, { operation: endpoint.id });
    }
    const status = result.status ?? (isWrite ? 201 : 200);
    if (isWrite && idemKey && claimLease !== null) {
        store.idempotency.complete(idemKey, {
            requestHash,
            status,
            body: result.body,
            storedAt: request.now,
        }, claimLease);
    }
    return {
        status,
        body: result.body,
        headers: { "content-type": "application/json" },
        operation: endpoint.id,
        replayed: false,
    };
}
/* ── An in-memory idempotency ledger, for mounts and for the gate ────────── */
/**
 * The in-process ledger, speaking the same reserve-then-complete protocol.
 *
 * ── It is not idle ceremony here, even though nothing in one process races ──
 *
 * Measured: thirty concurrent same-key pairs against a SINGLE host produced
 * exactly one work row, thirty times out of thirty. The dispatcher and every
 * handler it calls are synchronous, so `reserve` → handler → `complete` runs in
 * one turn of the event loop and cannot interleave with itself. That is a
 * property of the CURRENT handler contract, not a promise: the day a handler
 * returns a promise, the window opens, and this ledger is then the only thing
 * standing in it.
 *
 * So it implements the protocol properly — leases, fencing, takeover — rather
 * than stubbing the branches an in-process caller happens never to reach. A
 * ledger whose concurrency branches are unimplemented is a ledger that is right
 * by accident.
 */
export function createInMemoryIdempotencyLedger() {
    const map = new Map();
    const id = (k) => `${k.tenantId}\x00${k.tokenId}\x00${k.operation}\x00${k.key}`;
    return {
        reserve(k, input) {
            const rowKey = id(k);
            const lease = mintIdempotencyLease(input.now, input.leaseMs);
            const existing = map.get(rowKey);
            if (!existing) {
                map.set(rowKey, { requestHash: input.requestHash, record: null, lease });
                return { outcome: "reserved", lease };
            }
            if (existing.record)
                return { outcome: "replay", record: existing.record };
            if (existing.lease > normalizeIdempotencyInstant(input.now)) {
                return {
                    outcome: "in-progress",
                    requestHash: existing.requestHash,
                    leaseExpiresAt: existing.lease,
                };
            }
            // The lease expired: the holder died. Take it over — which means running
            // the handler ourselves, NOT replaying a response that was never stored.
            map.set(rowKey, { requestHash: input.requestHash, record: null, lease });
            return { outcome: "reserved", lease };
        },
        complete(k, record, lease) {
            const existing = map.get(id(k));
            // Fenced: a holder whose lease was taken over does not get to overwrite
            // the response the taker-over stored.
            if (!existing || existing.lease !== lease || existing.record)
                return;
            map.set(id(k), { requestHash: record.requestHash, record, lease });
        },
        release(k, lease) {
            const existing = map.get(id(k));
            if (!existing || existing.lease !== lease || existing.record)
                return;
            map.delete(id(k));
        },
        size: () => map.size,
    };
}
/**
 * Normalize an instant so two lease stamps are comparable as strings.
 *
 * Every stamp goes through `toISOString()`, which is fixed-width, so
 * lexicographic order IS chronological order. Mixing widths would not merely be
 * untidy — `"…00.000123Z" < "…00.000Z"` lexicographically while being LATER in
 * time, which would read an unexpired lease as expired and hand the key to a
 * second holder. A single format is what makes the comparison safe.
 *
 * An uninterpretable instant THROWS rather than falling back to wall-clock
 * time: a ledger that silently substitutes its own clock for the dispatcher's
 * is a ledger whose lease arithmetic nobody can reason about.
 */
export function normalizeIdempotencyInstant(now) {
    const ms = Date.parse(now);
    if (!Number.isFinite(ms)) {
        throw new Error(`idempotency ledger received an uninterpretable instant: ${JSON.stringify(now)}`);
    }
    return new Date(ms).toISOString();
}
/**
 * The lease stamp: when this reservation expires, and the token that fences it.
 *
 * One value doing both jobs is sound HERE and only here, because the two
 * holders who could ever compare stamps are separated by at least one whole
 * lease: a takeover happens only after the previous lease has expired, so the
 * taker-over's stamp is at least `leaseMs` beyond the holder's. Millisecond
 * resolution cannot collide across that gap. (Two holders arriving in the SAME
 * millisecond would mint the same stamp — but they cannot both hold the key,
 * because only one of them wins the insert.)
 */
export function mintIdempotencyLease(now, leaseMs) {
    const ms = Date.parse(now);
    if (!Number.isFinite(ms)) {
        throw new Error(`idempotency ledger received an uninterpretable instant: ${JSON.stringify(now)}`);
    }
    return new Date(ms + Math.max(0, leaseMs)).toISOString();
}
/**
 * The full catalogue annotated with what is actually served.
 *
 * Withheld operations are *listed*, not hidden. A surface that quietly omits
 * `receipts.issue` looks like a fourteen-endpoint API with a documentation gap;
 * a surface that shows it struck through with the reason is the product making
 * an argument about its own security model, which is the thing the CISO
 * persona is buying.
 *
 * The same argument is why `implemented` is on every row rather than only the
 * false ones: a reader has to be able to ask "does this do anything" of any
 * operation and get an answer, not infer it from a missing key.
 */
export function describeVerificationApiSurface() {
    return VERIFICATION_API_ENDPOINTS.map((e) => {
        const withheld = ENGINE_ONLY_OPERATIONS[e.id];
        const unimplemented = UNIMPLEMENTED_PUBLIC_OPERATIONS[e.id];
        const row = {
            id: e.id,
            method: e.method,
            path: e.path,
            requiredScope: e.requiredScope,
            summary: e.summary,
            stability: e.stability,
            resource: e.resource,
            served: withheld === undefined,
            implemented: unimplemented === undefined,
        };
        if (withheld !== undefined)
            row.withheldReason = withheld;
        if (unimplemented !== undefined)
            row.notImplementedReason = unimplemented;
        return row;
    });
}
